import * as THREE from "three";
import {
  createHingeJointVisualization,
  applyHingeJointValue,
  updateHingeJointVisibility,
} from "./hingeJoint";
import {
  createSlideJointVisualization,
  applySlideJointValue,
  updateSlideJointVisibility,
} from "./slideJoint";
import {
  createBallJointVisualization,
  applyBallJointValue,
  updateBallJointVisibility,
} from "./ballJoint";
import { logger } from "../../utils/logger";
import {
  JOINT_SPHERE_SIZES,
  JOINT_SPHERE_SEGMENTS,
  JOINT_COLORS,
  JOINT_LINE_PROPERTIES,
  JOINT_MATERIALS,
} from "./jointStyles";

/**
 * Joint factory for creating and managing different joint types
 */
export class JointFactory {
  /**
   * Creates visualization for a joint based on its type
   * @param {Object} jointInfo - Joint information object
   * @param {string} jointInfo.kind - Joint kind ('hinge', 'slide', etc.)
   * @param {THREE.Quaternion} parentRot - Parent rotation for world-space range indicators
   * @param {boolean} showJoints - Whether to show joint visualizations
   * @param {Object} jointMarkerSizes - Joint marker sizes
   * @returns {THREE.Group|null} Joint visualization group or null if type not supported
   */
  static createJointVisualization(
    jointInfo,
    parentRot,
    showJoints,
    jointMarkerSizes,
    visualizationOptions = null
  ) {
    switch (jointInfo.kind) {
      case "hinge":
        return createHingeJointVisualization(
          jointInfo,
          parentRot,
          showJoints,
          jointMarkerSizes,
          visualizationOptions
        );
      case "slide":
        return createSlideJointVisualization(jointInfo, parentRot, showJoints, jointMarkerSizes);
      case "ball":
        return createBallJointVisualization(jointInfo, parentRot, showJoints, jointMarkerSizes);
      case "free":
        return null;
      default:
        logger.warn(`Unknown joint kind: ${jointInfo.kind} for joint: ${jointInfo.name}`);
        return null;
    }
  }

  /**
   * Applies joint value based on joint type
   * @param {THREE.Group} bodyGroup - The body group to transform
   * @param {Object} joint - Joint information object
   * @param {number} value - Joint value
   * @param {THREE.Quaternion} initialRot - Initial rotation
   * @param {THREE.Vector3} initialPos - Initial position
   * @param {THREE.Quaternion} parentRot - Parent rotation
   */
  static applyJointValue(bodyGroup, joint, value, initialRot, initialPos, parentRot) {
    switch (joint.kind) {
      case "hinge":
        applyHingeJointValue(bodyGroup, joint, value, initialRot, initialPos);
        break;
      case "slide":
        applySlideJointValue(bodyGroup, joint, value, initialRot, initialPos, parentRot);
        break;
      case "ball":
        applyBallJointValue(bodyGroup, joint, value, initialRot, initialPos);
        break;
      case "free":
        // Support both no-op numeric value and full pose array [px,py,pz,qw,qx,qy,qz]
        if (Array.isArray(value) && value.length === 7) {
          const [px, py, pz, qw, qx, qy, qz] = value;
          bodyGroup.position.copy(initialPos.clone().add(new THREE.Vector3(px, py, pz)));
          bodyGroup.quaternion.copy(
            initialRot.clone().multiply(new THREE.Quaternion(qx, qy, qz, qw))
          );
        } else {
          // Keep current transform; free joints are controlled via base pose elsewhere
        }
        break;
      default:
        logger.warn(`Unknown joint kind: ${joint.kind} for joint: ${joint.name}`);
    }
  }

  /**
   * Updates joint visualization visibility based on joint type
   * @param {THREE.Group} jointVizGroup - Joint visualization group
   * @param {string} jointType - Joint type
   * @param {boolean} showJoints - Whether to show joint visualizations
   */
  static updateJointVisibility(jointVizGroup, jointType, showJoints) {
    switch (jointType) {
      case "hinge":
        updateHingeJointVisibility(jointVizGroup, showJoints);
        break;
      case "slide":
        updateSlideJointVisibility(jointVizGroup, showJoints);
        break;
      case "ball":
        updateBallJointVisibility(jointVizGroup, showJoints);
        break;
      case "free":
        break;
      default:
        logger.warn(`Unknown joint type for visibility update: ${jointType}`);
    }
  }

  /**
   * Checks if a joint type is supported
   * @param {string} jointType - Joint type to check
   * @returns {boolean} True if supported, false otherwise
   */
  static isJointTypeSupported(jointType) {
    const supportedTypes = ["hinge", "slide", "ball", "free"];
    return supportedTypes.includes(jointType);
  }

  /**
   * Gets list of supported joint types
   * @returns {Array<string>} Array of supported joint types
   */
  static getSupportedJointTypes() {
    return ["hinge", "slide", "ball", "free"];
  }
}

// Export styling constants for use in other modules
export {
  JOINT_SPHERE_SIZES,
  JOINT_SPHERE_SEGMENTS,
  JOINT_COLORS,
  JOINT_LINE_PROPERTIES,
  JOINT_MATERIALS,
};
